// DEPLOY-PROBE-bump

/**
 * SideBar Component
 * 
 * Navigation sidebar component that provides access to different sections of the
 * payslip management system. Adapts its behavior and appearance based on screen
 * size and user preferences, supporting both mobile and desktop layouts.
 * 
 * Features:
 * - Collapsible navigation menu for space efficiency on desktop
 * - Mobile-responsive design with burger menu and overlay behavior
 * - Visual navigation items with emoji icons and Japanese labels
 * - Active page highlighting for current section
 * - Smooth transitions and intuitive user experience
 * - Dynamic toggle icons (burger/close on mobile, arrows on desktop)
 * - Conditional label visibility when collapsed
 * 
 * Navigation items:
 * - 給与データ管理 (Salary Data Management) - 📊
 * - 従業員管理 (Employee Management) - 👥
 * - 給与明細 (Payroll Management) - 💰
 * - 書類交付 (Document delivery) - 📄
 * 
 * Behavior:
 * - Desktop: Sidebar can collapse to show only icons, with labels as tooltips
 * - Mobile: Sidebar shows as overlay menu with burger/close toggle
 * - Active page is highlighted with active class
 * 
 * Used by:
 * - MainLayout component for primary navigation
 * 
 * @component
 */


import './css/SideBar.css';

/**
 * SideBar Component Props Interface
 * 
 * @interface SideBarProps
 * @property {any} activePage - Currently active page identifier ('salarydata', 'employee', or 'payroll')
 * @property {(page: string) => void} setActivePage - Function to change the active page when navigation item is clicked
 * @property {boolean} isCollapsed - Whether sidebar is collapsed (true = collapsed, false = expanded)
 * @property {() => void} toggleSidebar - Function to toggle sidebar collapsed state
 * @property {boolean} [isMobile] - Optional flag indicating if current device is mobile (defaults to false)
 */
export const SideBar:React.FC<{
  activePage:any,                    // Currently active page identifier
  setActivePage: (page: string) => void,  // Function to change active page
  isCollapsed: boolean,              // Whether sidebar is collapsed
  toggleSidebar: () => void,        // Function to toggle sidebar state
  isMobile?: boolean                 // Whether current device is mobile
  isConstructionAdminOnly?: boolean // When true (現場管理者), hide consent list
  /** 担当現場管理者: only payroll, my payslips, account */
  isAssignedSiteAdminOnly?: boolean
  showAccountSettingsNav?: boolean // When false (レベル1管理者), hide アカウント設定 (self-service via 従業員管理)
  showSecurityNav?: boolean         // When true (Level 1 admin), show セキュリティ
  onSecurityClick?: () => void      // When Security nav is clicked, open password gate
  showDocumentsNav?: boolean        // 書類交付 for 管理者（上位権限） and 管理者（現場権限）
}> = (props)  =>{
  
  const allNavItems = [
    { id: 'salarydata', label: '給与データ管理', icon: '📊' },
    { id: 'employee', label: '従業員管理', icon: '👥' },
    { id: 'payroll', label: '給与明細', icon: '💰' },
    { id: 'documents', label: '書類交付', icon: '📄' },
    { id: 'myPayslips', label: '自分の帳票', icon: '🧾' },
    { id: 'account', label: 'アカウント設定', icon: '⚙️' },
    { id: 'consent', label: '電子交付同意一覧', icon: '📋' },
    { id: 'security', label: 'セキュリティ', icon: '🔐' }
  ];
  let navItems = props.isAssignedSiteAdminOnly
    ? allNavItems.filter((item) => item.id === 'payroll' || item.id === 'myPayslips' || item.id === 'account')
    : props.isConstructionAdminOnly
    ? allNavItems.filter((item) => item.id !== 'consent')
    : allNavItems;
  if (props.showAccountSettingsNav === false) {
    navItems = navItems.filter((item) => item.id !== 'account');
  }
  if (!props.showSecurityNav) {
    navItems = navItems.filter((item) => item.id !== 'security');
  }
  if (!props.showDocumentsNav) {
    navItems = navItems.filter((item) => item.id !== 'documents');
  }

  /**
   * Determines the appropriate toggle icon based on device type and sidebar state.
   * 
   * Returns different icons based on device type:
   * - Mobile: Burger menu (☰) when collapsed, close (✕) when expanded
   * - Desktop: Right arrow (→) when collapsed, left arrow (←) when expanded
   * 
   * This provides intuitive visual feedback about the sidebar's state and action.
   * 
   * @returns {string} The appropriate icon for the current state
   * 
   * @example
   * ```typescript
   * // Mobile, collapsed -> '☰'
   * // Mobile, expanded -> '✕'
   * // Desktop, collapsed -> '→'
   * // Desktop, expanded -> '←'
   * ```
   */
  const getToggleIcon = () => {
    if (props.isMobile) {
      return props.isCollapsed ? '☰' : '✕'; // Burger when collapsed, X when expanded
    }
    return props.isCollapsed ? '→' : '←'; // Arrows for desktop
  };

  return (
    /**
     * Sidebar container with dynamic classes based on state.
     * - 'collapsed': Applied when sidebar is collapsed
     * - 'mobile': Applied when device is mobile
     */
    <div className={`sidebar ${props.isCollapsed ? 'collapsed' : ''} ${props.isMobile ? 'mobile' : ''}`}>
      {/* Sidebar Header - Contains title and toggle button */}
      <div className="sidebar-header">
        <div className="header-content">
          {/* System Title - "Payslip System" (給与明細システム) */}
          {/* Hidden when collapsed on desktop for space efficiency */}
          <h3 className={props.isCollapsed && !props.isMobile ? 'hidden' : ''}>給与明細システム</h3>
        </div>
        
        {/* Toggle Button - Collapses/expands sidebar on click */}
        {/* Icon changes based on device type and state (burger/X on mobile, arrows on desktop) */}
        <button 
          className="sidebar-toggle"
          onClick={props.toggleSidebar}
          title={props.isMobile 
            ? (props.isCollapsed ? 'Open navigation menu' : 'Close navigation menu')
            : (props.isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')
          }
          aria-label={props.isMobile 
            ? (props.isCollapsed ? 'Open navigation menu' : 'Close navigation menu')
            : (props.isCollapsed ? 'Expand sidebar' : 'Collapse sidebar')
          }
        >
          {getToggleIcon()}
        </button>
      </div>
      
      {/* Main Navigation Menu - Contains navigation items */}
      <nav className="sidebar-nav" aria-label="Main navigation">
        <ul>
          {/* Render Navigation Items Dynamically */}
          {/* Maps through navItems array to create navigation buttons */}
          {navItems.map((item) => (
            <li key={item.id}>
              {/* Navigation Item Button */}
              {/* Active class applied when this item's id matches activePage */}
              <button 
                className={`nav-item ${props.activePage === item.id ? 'active' : ''}`}
                onClick={() => {
                  if (item.id === 'security' && props.onSecurityClick) {
                    props.onSecurityClick();
                  } else {
                    props.setActivePage(item.id);
                  }
                }}
                title={props.isCollapsed && !props.isMobile ? item.label : ''}
                aria-label={item.label}
                aria-current={props.activePage === item.id ? 'page' : undefined}
              >
                {/* Navigation Icon - Always visible, provides visual identification */}
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                
                {/* Navigation Label - Japanese text for the navigation item */}
                {/* Hidden when collapsed on desktop, shown as tooltip on hover */}
                <span className={`nav-label ${props.isCollapsed && !props.isMobile ? 'hidden' : ''}`}>
                  {item.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* Sidebar Footer - Company copyright information */}
      <div className="sidebar-footer">
        {/* Company Copyright - "© 2026 Nishiyuki Civil Engineering Co., Ltd." */}
        {/* Hidden when collapsed on desktop for space efficiency */}
        <p className={props.isCollapsed && !props.isMobile ? 'hidden' : ''}>
          © 2026 株式会社西行土木
        </p>
      </div>
    </div>
  );
}

